import React, { useState } from 'react';
import { Box, TextField, IconButton, InputAdornment, Typography } from '@mui/material';
import { Send as SendIcon, InsertDriveFile as FileIcon, Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { clearFileContext } from '../../redux/aiChat/aiChatSlice';

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f8f9ff',
  borderTop: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '24px',
    backgroundColor: '#eff4ff',
    '& fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: `1px solid ${'#97c96a'}`,
      opacity: 0.2,
    },
    paddingRight: theme.spacing(1),
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    fontFamily: 'Inter, sans-serif',
  },
}));

interface ChatInputProps {
  onSendMessage: (message: string, fileContext?: string | null) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled }) => {
  const [message, setMessage] = useState('');
  const fileContext = useSelector((state: RootState) => state.aiChat.fileContext);
  const dispatch = useDispatch();

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message, fileContext);
      setMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <InputContainer>
      {fileContext && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.5,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'primary.light',
          }}
        >
          <FileIcon sx={{ fontSize: '1rem', color: '#97c96a' }} />
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontFamily: 'Inter, sans-serif',
              color: 'primary.dark',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 260,
            }}
          >
            {fileContext.split('/').pop() ?? fileContext}
          </Typography>
          <IconButton
            size="small"
            onClick={() => dispatch(clearFileContext())}
            sx={{ p: 0.25, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        </Box>
      )}
      <StyledTextField
        fullWidth
        placeholder={fileContext ? 'Ask something about this file...' : 'Type a message...'}
        variant="outlined"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton 
                onClick={handleSend} 
                disabled={disabled || !message.trim()}
                color="primary"
                sx={{ 
                  bgcolor: '#97c96a', 
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'grey.300',
                    color: 'white',
                  },
                  width: 32,
                  height: 32,
                  padding: 0
                }}
              >
                <SendIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </InputContainer>
  );
};

export default ChatInput;
